import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Save, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { LoadError, useAdminData } from '@/domains/admin';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import type {
  ContentEventTemplate,
  IContentEvent,
  IContentEventBody,
  IContentEventSessionBody,
  IContentEventSpeakerBody
} from '../api/event-contracts';
import { eventsApi } from '../api/events-api';
import type { IContentImageRef } from '../api/media-contracts';
import { ImagePicker } from '../library';
import { EditPresenceNotice, useEditPresence } from '../presence';
import { CONTENT_PATHS } from '../paths';
import {
  ConflictNotice,
  EmptyNote,
  Field,
  FieldRefusal,
  HistoryPanel,
  SeoFields,
  StatusPill,
  personName,
  slugify
} from '../shared';
import { ContentPage } from './content-page';

/**
 * One event, authored (WEB-08), over `/api/v1/admin/website/events`.
 *
 * **Its own address, like a post's.** An event page is somewhere an author stays: the detail, a
 * programme of a dozen sessions and a speaker list are not an expanding row in a list.
 *
 * **One save carries the whole programme.** The agenda and the speakers are members of the
 * event's body on the wire, so adding an item, removing one, moving one and retiming another are
 * a single write — which is also why this screen has one Save button and no per-row saving. An
 * item dropped from the list is an item removed; the server assigns positions from the order it
 * arrives in.
 *
 * **The agenda is moved, never numbered.** Two buttons beside each row rather than a drag: the
 * team page's ruling, for its reason — a drag is not reachable from a keyboard.
 *
 * **Publishing is somebody else's permission.** `content.publish` decides whether the two site
 * controls are offered, as it does on a post; survey-api checks it again, and refuses to publish
 * an archived event at all.
 */

const TEMPLATES: { value: ContentEventTemplate; label: string }[] = [
  { value: 'national', label: 'National' },
  { value: 'regional', label: 'Regional' },
  { value: 'exclusive', label: 'Exclusive' }
];

export function ContentEventEditorPage() {
  const { eventId } = useParams();
  if (!eventId) return <Navigate to={CONTENT_PATHS.events} replace />;
  return <EventLoader key={eventId} eventId={eventId} />;
}

function EventLoader({ eventId }: { eventId: string }) {
  const { data, error, adopt } = useAdminData(
    (signal) => eventsApi.read(eventId, signal),
    [eventId]
  );

  if (error) {
    return (
      <ContentPage tabs={false} crumb="Events" title="Event">
        <LoadError message={error} />
      </ContentPage>
    );
  }
  if (!data) return null;
  return <EventEditor happening={data.body} etag={data.etag} onWrote={adopt} />;
}

/** The record as a body: what the form edits, and what a save sends back. */
function bodyOf(happening: IContentEvent): IContentEventBody {
  return {
    name: happening.name,
    startsAt: happening.startsAt,
    endsAt: happening.endsAt,
    venue: happening.venue,
    city: happening.city,
    state: happening.state,
    description: happening.description,
    registrationUrl: happening.registrationUrl,
    template: happening.template,
    // The ids are dropped deliberately: a save describes the programme it wants and the server
    // rebuilds the list from it, so sending a row id back would be sending it something it is
    // about to replace.
    sessions: happening.sessions.map((session) => ({
      time: session.time,
      title: session.title,
      description: session.description
    })),
    speakers: happening.speakers.map((speaker) => ({
      name: speaker.name,
      title: speaker.title,
      bio: speaker.bio,
      headshotId: speaker.headshot?.imageId ?? null
    })),
    slug: happening.slug,
    seo: {
      title: happening.seo.title,
      description: happening.seo.description,
      imageId: happening.seo.image?.imageId ?? null
    }
  };
}

function EventEditor({
  happening,
  etag,
  onWrote
}: {
  happening: IContentEvent;
  etag: Nullable<string>;
  onWrote: (next: { body: IContentEvent; etag: Nullable<string> }) => void;
}) {
  const can = useCan();
  const [form, setForm] = React.useState<IContentEventBody>(() => bodyOf(happening));
  const [busy, setBusy] = React.useState(false);
  const [conflict, setConflict] = React.useState<Nullable<string>>(null);
  const [refusals, setRefusals] = React.useState<{ target: string; reason: string }[]>([]);
  const [done, setDone] = React.useState<Nullable<string>>(null);

  const others = useEditPresence('event', happening.eventId);

  const archived = happening.status === 'archived';
  const draft = happening.status === 'draft';
  const dirty = JSON.stringify(form) !== JSON.stringify(bodyOf(happening));
  const set = <K extends keyof IContentEventBody>(key: K, value: IContentEventBody[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const refusalOf = (target: string) => refusals.find((problem) => problem.target === target);

  /** The address follows the title until the event is published, and then stops for good — the
   *  post editor's rule, and only once the title changes, for its reason. */
  const named = React.useRef(form.name);
  React.useEffect(() => {
    if (!draft || named.current === form.name) return;
    named.current = form.name;
    setForm((current) => ({ ...current, slug: slugify(current.name) }));
  }, [form.name, draft]);

  /** Re-reads and reseeds the form — the post editor's way out of a conflict, and its reason:
   *  reloading while leaving the typed text in place would say it had loaded their version while
   *  still holding the one that was refused. */
  const reload = async () => {
    const fresh = await eventsApi.read(happening.eventId);
    onWrote(fresh);
    setForm(bodyOf(fresh.body));
    setConflict(null);
    setRefusals([]);
    setDone(null);
  };

  const write = async (
    run: (precondition: string) => Promise<{ body: IContentEvent; etag: Nullable<string> }>,
    said: string
  ) => {
    if (!etag) {
      setConflict('this page was loaded without its revision');
      return;
    }
    setBusy(true);
    setConflict(null);
    setRefusals([]);
    try {
      const answer = await run(etag);
      onWrote(answer);
      setForm(bodyOf(answer.body));
      setDone(said);
    } catch (error) {
      setDone(null);
      if (error instanceof ApiError && error.isProblem(ProblemType.revisionConflict)) {
        setConflict(personName(happening.updatedBy));
      } else if (
        error instanceof ApiError &&
        (error.isProblem(ProblemType.invalidBody) || error.isProblem(ProblemType.codeInUse))
      ) {
        setRefusals(error.problem?.problems ?? [{ target: '', reason: 'was refused.' }]);
      } else {
        setRefusals([{ target: '', reason: 'could not be saved. Try again.' }]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ContentPage
      tabs={false}
      crumb="Events"
      title={form.name || 'Untitled event'}
      actions={
        <>
          <Button variant="ghost" size="sm" asChild>
            <Link to={CONTENT_PATHS.events}>
              <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
              All events
            </Link>
          </Button>
          <Button
            size="sm"
            onClick={() =>
              void write(
                (precondition) => eventsApi.save(happening.eventId, precondition, form),
                'Saved.'
              )
            }
            disabled={!dirty || busy || archived}
          >
            <Save className="mr-1 h-4 w-4" aria-hidden />
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-3">
        <StatusPill status={happening.status} />
        {done && <p className="text-sm text-muted-foreground">{done}</p>}
      </div>

      <EditPresenceNotice kind="event" others={others} />
      {conflict && <ConflictNotice by={conflict} onReload={() => void reload()} />}
      {refusalOf('') && <FieldRefusal reason={refusalOf('')!.reason} />}

      {archived && (
        <EmptyNote>
          This event is archived and cannot be edited. Bring it back as a draft first — what is
          wrong with it cannot be fixed while it is off the site.
        </EmptyNote>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">The event</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <Field id="name" label="Title" refusal={refusalOf('name')}>
            <Input
              id="name"
              value={form.name}
              disabled={archived}
              onChange={(e) => set('name', e.target.value)}
            />
          </Field>

          <Field id="slug" label="Address" refusal={refusalOf('slug')}>
            <Input id="slug" value={form.slug ?? ''} readOnly disabled />
          </Field>
          <p className="-mt-2 text-xs text-muted-foreground">
            /events/{form.slug} — derived from the title, and frozen once the event is on the
            site: it is cited in invitations.
          </p>

          {/* Dates and times together: a programme firms up by the hour, and a start without an
              end is the mistake worth making visible side by side. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="startsAt" label="Starts" refusal={refusalOf('startsAt')}>
              <Input
                id="startsAt"
                type="datetime-local"
                value={forInput(form.startsAt)}
                disabled={archived}
                onChange={(e) => set('startsAt', fromInput(e.target.value) || form.startsAt)}
              />
            </Field>
            <Field id="endsAt" label="Ends" refusal={refusalOf('endsAt')}>
              <Input
                id="endsAt"
                type="datetime-local"
                value={forInput(form.endsAt)}
                disabled={archived}
                onChange={(e) => set('endsAt', fromInput(e.target.value) || form.endsAt)}
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field id="venue" label="Venue" refusal={refusalOf('venue')}>
              <Input
                id="venue"
                value={form.venue}
                disabled={archived}
                onChange={(e) => set('venue', e.target.value)}
              />
            </Field>
            <Field id="city" label="City" refusal={refusalOf('city')}>
              <Input
                id="city"
                value={form.city}
                disabled={archived}
                onChange={(e) => set('city', e.target.value)}
              />
            </Field>
            <Field id="state" label="State" refusal={refusalOf('state')}>
              <Input
                id="state"
                value={form.state}
                disabled={archived}
                onChange={(e) => set('state', e.target.value)}
              />
            </Field>
          </div>

          <Field id="description" label="Description" refusal={refusalOf('description')}>
            <Textarea
              id="description"
              rows={4}
              value={form.description}
              disabled={archived}
              onChange={(e) => set('description', e.target.value)}
            />
          </Field>

          <Field
            id="registrationUrl"
            label="Registration link"
            refusal={refusalOf('registrationUrl')}
          >
            <Input
              id="registrationUrl"
              type="url"
              placeholder="https://…"
              value={form.registrationUrl}
              disabled={archived}
              onChange={(e) => set('registrationUrl', e.target.value)}
            />
          </Field>
          <p className="-mt-2 text-xs text-muted-foreground">
            Where registration happens. The public page embeds this form rather than collecting
            names itself.
          </p>

          <Field id="template" label="Template" refusal={refusalOf('template')}>
            <Select
              value={form.template}
              disabled={archived}
              onValueChange={(value) => set('template', value as ContentEventTemplate)}
            >
              <SelectTrigger id="template">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATES.map((template) => (
                  <SelectItem key={template.value} value={template.value}>
                    {template.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </CardContent>
      </Card>

      <AgendaEditor
        sessions={form.sessions}
        disabled={archived}
        refusalOf={refusalOf}
        onChange={(sessions) => set('sessions', sessions)}
      />

      <SpeakerEditor
        speakers={form.speakers}
        // By id rather than by position: a speaker moved up the list is still holding the
        // same face, and an index would hand them somebody else's.
        placed={(imageId) =>
          happening.speakers.find((one) => one.headshot?.imageId === imageId)?.headshot ?? null
        }
        disabled={archived}
        refusalOf={refusalOf}
        onChange={(speakers) => set('speakers', speakers)}
      />

      <SeoFields
        seo={form.seo}
        onChange={(seo) => set('seo', seo)}
        pageTitle={form.name}
        socialImageId={form.seo.imageId}
        socialImage={happening.seo.image}
        onPickSocialImage={(imageId) => set('seo', { ...form.seo, imageId })}
      />

      {/* The two site controls, and the one that brings an archived event back. Drawn only where
          the caller holds the permission survey-api would check anyway: a button that always
          answers 403 is worse than no button. */}
      {can('content.publish') && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">On the site</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-3">
            {happening.status === 'published' ? (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() =>
                  void write(
                    (precondition) => eventsApi.archive(happening.eventId, precondition),
                    'Archived. It is off the listing and every word is kept.'
                  )
                }
              >
                Archive
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={busy || archived || dirty}
                onClick={() =>
                  void write(
                    (precondition) => eventsApi.publish(happening.eventId, precondition),
                    'On the site.'
                  )
                }
              >
                Put it on the site
              </Button>
            )}
            {dirty && happening.status !== 'published' && !archived && (
              <p className="text-xs text-muted-foreground">Save first.</p>
            )}
          </CardContent>
        </Card>
      )}

      {archived && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Archived</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                void write(
                  (precondition) => eventsApi.restore(happening.eventId, precondition),
                  'Back as a draft.'
                )
              }
            >
              Bring it back
            </Button>
            <p className="text-xs text-muted-foreground">
              It returns as a draft, never straight onto the site.
            </p>
          </CardContent>
        </Card>
      )}

      <HistoryPanel
        // The panel says who in words; a post carries an email address and a mock a name, and it
        // is not the place to learn the difference.
        history={happening.history.map((version) => ({
          versionNo: version.versionNo,
          at: version.at,
          by: personName(version.by),
          action: version.action,
          note: version.note
        }))}
        onRevert={(versionNo) =>
          void write(
            (precondition) => eventsApi.revert(happening.eventId, precondition, versionNo),
            `Restored version ${versionNo}.`
          )
        }
      />
    </ContentPage>
  );
}

/* ── the agenda (AC2) ──────────────────────────────────────────────────────────────────── */

function AgendaEditor({
  sessions,
  disabled,
  refusalOf,
  onChange
}: {
  sessions: IContentEventSessionBody[];
  disabled: boolean;
  refusalOf: (target: string) => { reason: string } | undefined;
  onChange: (next: IContentEventSessionBody[]) => void;
}) {
  const edit = (index: number, patch: Partial<IContentEventSessionBody>) =>
    onChange(sessions.map((item, at) => (at === index ? { ...item, ...patch } : item)));

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm">Agenda ({sessions.length})</CardTitle>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...sessions, { time: '', title: '', description: '' }])}
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          Add an item
        </Button>
      </CardHeader>
      <CardContent className="grid gap-3">
        {refusalOf('sessions') && <FieldRefusal reason={refusalOf('sessions')!.reason} />}
        {sessions.length === 0 && (
          <EmptyNote>No agenda yet. Add the first item as the programme firms up.</EmptyNote>
        )}
        {/* Keyed by position, not by a row id: a save replaces the list, so an item has no
            identity of its own between one save and the next. */}
        {sessions.map((item, index) => (
          <div key={index} className="grid gap-3 rounded-md border border-border p-3">
            <div className="flex flex-wrap items-start gap-3">
              <div className="w-32 shrink-0">
                <Field
                  id={`time-${index}`}
                  label="Time"
                  refusal={refusalOf(`sessions[${index}].time`)}
                >
                  <Input
                    id={`time-${index}`}
                    placeholder="09:00"
                    value={item.time}
                    disabled={disabled}
                    onChange={(e) => edit(index, { time: e.target.value })}
                  />
                </Field>
              </div>
              <div className="min-w-0 flex-1">
                <Field
                  id={`session-${index}`}
                  label="Session"
                  refusal={refusalOf(`sessions[${index}].title`)}
                >
                  <Input
                    id={`session-${index}`}
                    value={item.title}
                    disabled={disabled}
                    onChange={(e) => edit(index, { title: e.target.value })}
                  />
                </Field>
              </div>
              <RowControls
                index={index}
                count={sessions.length}
                what="agenda item"
                disabled={disabled}
                onMove={(by) => onChange(moved(sessions, index, by))}
                onRemove={() => onChange(sessions.filter((_, at) => at !== index))}
              />
            </div>
            <Field
              id={`detail-${index}`}
              label="Description"
              refusal={refusalOf(`sessions[${index}].description`)}
            >
              <Textarea
                id={`detail-${index}`}
                rows={2}
                value={item.description}
                disabled={disabled}
                onChange={(e) => edit(index, { description: e.target.value })}
              />
            </Field>
          </div>
        ))}
        <p className="text-xs text-muted-foreground">
          A time is text, so &ldquo;After lunch&rdquo; and &ldquo;Evening&rdquo; are as valid as
          &ldquo;09:00&rdquo;.
        </p>
      </CardContent>
    </Card>
  );
}

/* ── the speakers (AC3) ────────────────────────────────────────────────────────────────── */

function SpeakerEditor({
  speakers,
  placed,
  disabled,
  refusalOf,
  onChange
}: {
  speakers: IContentEventSpeakerBody[];
  /** The headshot the record answered for this id — see `ImagePicker`'s `current`. */
  placed: (imageId: Nullable<string>) => Nullable<IContentImageRef>;
  disabled: boolean;
  refusalOf: (target: string) => { reason: string } | undefined;
  onChange: (next: IContentEventSpeakerBody[]) => void;
}) {
  const edit = (index: number, patch: Partial<IContentEventSpeakerBody>) =>
    onChange(speakers.map((one, at) => (at === index ? { ...one, ...patch } : one)));

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm">Speakers ({speakers.length})</CardTitle>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() =>
            onChange([...speakers, { name: '', title: '', bio: '', headshotId: null }])
          }
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          Add a speaker
        </Button>
      </CardHeader>
      <CardContent className="grid gap-3">
        {refusalOf('speakers') && <FieldRefusal reason={refusalOf('speakers')!.reason} />}
        {speakers.length === 0 && <EmptyNote>No speakers announced yet.</EmptyNote>}
        {speakers.map((one, index) => (
          <div key={index} className="grid gap-3 rounded-md border border-border p-3">
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1">
                <Field
                  id={`name-${index}`}
                  label="Name"
                  refusal={refusalOf(`speakers[${index}].name`)}
                >
                  <Input
                    id={`name-${index}`}
                    value={one.name}
                    disabled={disabled}
                    onChange={(e) => edit(index, { name: e.target.value })}
                  />
                </Field>
              </div>
              <div className="min-w-0 flex-1">
                <Field
                  id={`role-${index}`}
                  label="Title"
                  refusal={refusalOf(`speakers[${index}].title`)}
                >
                  <Input
                    id={`role-${index}`}
                    value={one.title}
                    disabled={disabled}
                    onChange={(e) => edit(index, { title: e.target.value })}
                  />
                </Field>
              </div>
              <RowControls
                index={index}
                count={speakers.length}
                what="speaker"
                disabled={disabled}
                onMove={(by) => onChange(moved(speakers, index, by))}
                onRemove={() => onChange(speakers.filter((_, at) => at !== index))}
              />
            </div>
            <Field
              id={`bio-${index}`}
              label="Biography"
              refusal={refusalOf(`speakers[${index}].bio`)}
            >
              <Textarea
                id={`bio-${index}`}
                rows={3}
                value={one.bio}
                disabled={disabled}
                onChange={(e) => edit(index, { bio: e.target.value })}
              />
            </Field>
            {/* The same library the team page's photographs come from, narrowed to headshots so
                an event photograph is not offered where a face belongs. */}
            <ImagePicker
              label="Headshot"
              value={one.headshotId}
              current={placed(one.headshotId)}
              kinds={['headshot']}
              disabled={disabled}
              onPick={(headshotId) => edit(index, { headshotId })}
            />
            {refusalOf(`speakers[${index}].headshotId`) && (
              <FieldRefusal reason={refusalOf(`speakers[${index}].headshotId`)!.reason} />
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/* ── what every row of both lists needs ────────────────────────────────────────────────── */

function RowControls({
  index,
  count,
  what,
  disabled,
  onMove,
  onRemove
}: {
  index: number;
  count: number;
  /** Named in every label, so a screen reader hears which list is being reordered. */
  what: string;
  disabled: boolean;
  onMove: (by: number) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1 pt-6">
      <Button
        variant="outline"
        size="icon"
        aria-label={`Move this ${what} up`}
        disabled={disabled || index === 0}
        onClick={() => onMove(-1)}
      >
        <ArrowUp className="h-4 w-4" aria-hidden />
      </Button>
      <Button
        variant="outline"
        size="icon"
        aria-label={`Move this ${what} down`}
        disabled={disabled || index === count - 1}
        onClick={() => onMove(1)}
      >
        <ArrowDown className="h-4 w-4" aria-hidden />
      </Button>
      <Button
        variant="outline"
        size="icon"
        aria-label={`Remove this ${what}`}
        disabled={disabled}
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </Button>
    </div>
  );
}

/** One row moved by one place, as a copy: the caller hands the result straight to the form, and
 *  mutating the array it was given would change what React is comparing against. */
function moved<T>(rows: T[], index: number, by: number): T[] {
  const to = index + by;
  if (to < 0 || to >= rows.length) return rows;
  const next = [...rows];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

/** An instant as `datetime-local` wants it: the local wall clock, no zone and no seconds. */
function forInput(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}` +
    `T${pad(at.getHours())}:${pad(at.getMinutes())}`
  );
}

/** Back the other way, or empty where the control is mid-edit. `Date` reads the control's value
 *  as a local wall clock, so what is sent is the instant the author meant where they are. */
function fromInput(value: string): string {
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? '' : at.toISOString();
}
